"use client";

import { useEffect, useRef, useState, useCallback } from "react";

// =============================================================================
// HEART BUTTON
// =============================================================================
// Spam-clickable heart with persistent count, backed by Abacus (free third-party
// counter API). Each post gets its own counter keyed by postId.
//
// Behavior:
//   - On mount: GET current value. If 404 (counter doesn't exist yet), start at 0.
//   - On click: optimistic ++; queue a /hit/ request.
//   - Flush loop: fire one /hit/ every ~150ms until queue drains. Prevents
//     hammering the service if someone spam-clicks.
//   - Particles: each click spawns a floating heart that drifts up and fades.
//
// Service: https://abacus.jasoncameron.dev
//   - GET /hit/{namespace}/{key}   → increments, returns { value }
//   - GET /get/{namespace}/{key}   → reads, returns { value } or 404
// If the service ever disappears, swap the three URLs and the interface stays
// the same.

const ABACUS_BASE = "https://abacus.jasoncameron.dev";
const NAMESPACE = "vincent-portfolio-hearts";
const FLUSH_INTERVAL_MS = 150;
const PARTICLE_LIFETIME_MS = 900;

function formatCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10_000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  if (n < 1_000_000) return Math.floor(n / 1000) + "k";
  return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "m";
}

interface Particle {
  id: number;
  drift: number; // -1 to 1, horizontal offset
}

interface HeartButtonProps {
  postId: string;
}

export function HeartButton({ postId }: HeartButtonProps) {
  const [count, setCount] = useState<number | null>(null); // null = loading
  const [particles, setParticles] = useState<Particle[]>([]);
  const [pulse, setPulse] = useState(0); // rerenders to trigger scale animation
  const pendingRef = useRef(0);
  const flushingRef = useRef(false);
  const particleIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const key = `heart-${postId}`;

  // Initial read. 404 means no counter yet — treat as 0.
  useEffect(() => {
    const ac = new AbortController();
    abortRef.current = ac;

    fetch(`${ABACUS_BASE}/get/${NAMESPACE}/${key}`, { signal: ac.signal })
      .then(async (res) => {
        if (res.status === 404) {
          setCount(0);
          return;
        }
        if (!res.ok) throw new Error(`read failed: ${res.status}`);
        const data = (await res.json()) as { value: number };
        setCount(data.value ?? 0);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        // Fail open: show 0 so the button is still usable even if the service
        // is unreachable. Clicks still queue; they'll fail quietly.
        setCount(0);
      });

    return () => ac.abort();
  }, [key]);

  // Drain the pending queue at FLUSH_INTERVAL_MS. One /hit/ = +1 on the server.
  // We don't rely on the server's returned value to update the UI — the local
  // count is authoritative during a click burst, because the server is lagging.
  const flush = useCallback(() => {
    if (flushingRef.current) return;
    flushingRef.current = true;

    const tick = () => {
      if (pendingRef.current <= 0) {
        flushingRef.current = false;
        return;
      }
      pendingRef.current -= 1;
      fetch(`${ABACUS_BASE}/hit/${NAMESPACE}/${key}`).catch(() => {
        // Silent failure. Local count already bumped; we're not reconciling.
      });
      setTimeout(tick, FLUSH_INTERVAL_MS);
    };

    tick();
  }, [key]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    // Stop the lightbox's backdrop-click-to-close from firing.
    e.stopPropagation();
    if (count === null) return; // still loading
    setCount((c) => (c ?? 0) + 1);
    pendingRef.current += 1;
    setPulse((p) => p + 1);

    // Spawn particle
    const id = ++particleIdRef.current;
    const drift = (Math.random() - 0.5) * 2; // -1 to 1
    setParticles((prev) => [...prev, { id, drift }]);
    setTimeout(() => {
      setParticles((prev) => prev.filter((p) => p.id !== id));
    }, PARTICLE_LIFETIME_MS);

    flush();
  }, [count, flush]);

  const isLoading = count === null;
  const displayCount = count ?? 0;
  const [hovered, setHovered] = useState(false);

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      disabled={isLoading}
      aria-label={`Heart this post (${displayCount} hearts)`}
      className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono font-bold tracking-widest uppercase select-none"
      style={{
        background: hovered ? "rgba(255, 77, 109, 0.22)" : "rgba(255, 77, 109, 0.12)",
        color: "#FF4D6D",
        border: `1px solid ${hovered ? "rgba(255, 77, 109, 0.55)" : "rgba(255, 77, 109, 0.35)"}`,
        cursor: isLoading ? "wait" : "pointer",
        transition: "background 0.15s ease, border-color 0.15s ease",
      }}
    >
      <span
        key={pulse}
        aria-hidden="true"
        style={{
          display: "inline-block",
          animation: pulse > 0 ? "heart-pulse 220ms ease-out" : undefined,
          transformOrigin: "center",
        }}
      >
        {/* Solid heart SVG */}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
        </svg>
      </span>
      <span>{isLoading ? "…" : formatCount(displayCount)}</span>

      {/* Particle field — floating hearts on each click */}
      <span
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 pointer-events-none"
        style={{ transform: "translate(-50%, -50%)" }}
      >
        {particles.map((p) => (
          <span
            key={p.id}
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              animation: `heart-float ${PARTICLE_LIFETIME_MS}ms ease-out forwards`,
              // CSS custom props feed the keyframes so each particle drifts differently
              ["--drift" as string]: `${p.drift * 24}px`,
              color: "#FF4D6D",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          </span>
        ))}
      </span>

      <style jsx>{`
        @keyframes heart-pulse {
          0% { transform: scale(1); }
          40% { transform: scale(1.3); }
          100% { transform: scale(1); }
        }
        @keyframes heart-float {
          0% {
            transform: translate(-50%, -50%) scale(0.6);
            opacity: 0;
          }
          15% {
            opacity: 1;
          }
          100% {
            transform: translate(calc(-50% + var(--drift)), calc(-50% - 40px)) scale(1);
            opacity: 0;
          }
        }
      `}</style>
    </button>
  );
}
